import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import { TourController } from '@/components/Tour/TourController';
import { FloatingChatButton, ChatDrawer } from '@/components/AIChat';
import { useAuth } from '@/context/AuthContext';
import { useTenantContext } from '@/context/TenantContext';
import ProtectedRoute from '@/routes/ProtectedRoute';
import FeatureGuard from './components/FeatureGuard';
import RouteErrorPage from './pages/Error/RouteErrorPage';
import { lazyWithRetry } from './utils/lazyWithRetry';
import ForbiddenPage from './pages/errors/ForbiddenPage';

// Lazy loading com retry automático em caso de erro de chunk
// Resolve "Failed to fetch dynamically imported module" após deploys
const HomePage = lazyWithRetry(() => import('./pages/Home/HomePage'));
const ProjectsPage = lazyWithRetry(() => import('./pages/Projects/ProjectsPage'));
const ProjectCreatePage = lazyWithRetry(() => import('./pages/Projects/ProjectCreatePage'));
const ProjectsRoadmapPage = lazyWithRetry(() => import('./pages/Projects/RoadmapPage'));
const ReportsPage = lazyWithRetry(() => import('./pages/Reports/ReportsPage'));
const PortfolioAIReportPage = lazyWithRetry(() => import('./pages/Reports/PortfolioAIReportPage'));
const PublicFAQPage = lazyWithRetry(() => import('./src/pages/Public/FAQPublicPage'));
const PublicPrivacyPage = lazyWithRetry(() => import('./src/pages/Public/PrivacyPage'));
const PublicTermsPage = lazyWithRetry(() => import('./src/pages/Public/TermsPage'));
const PublicCookiesPage = lazyWithRetry(() => import('./src/pages/Public/CookiesPage'));
const ProjectPage = lazyWithRetry(() => import('./pages/Project/ProjectPage'));
const LoginPage = lazyWithRetry(() => import('./pages/Auth/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('./pages/Auth/RegisterPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/Auth/ResetPasswordPage'));
const UserApprovalsPage = lazyWithRetry(() => import('./pages/Admin/UserApprovalsPage'));
const ResponsibleRegistryPage = lazyWithRetry(() => import('./pages/Admin/ResponsibleRegistryPage'));
const CostGroupRegistryPage = lazyWithRetry(() => import('./pages/Admin/CostGroupRegistryPage'));
const AlertsConfigPage = lazyWithRetry(() => import('./pages/Admin/AlertsConfigPage'));
const SettingsPage = lazyWithRetry(() => import('./pages/Admin/SettingsPage'));
const UserProfilePage = lazyWithRetry(() => import('./pages/Profile/UserProfilePage'));
const IntakeListPage = lazyWithRetry(() => import('./pages/Intake/IntakeListPage'));
const CommitteeListPage = lazyWithRetry(() => import('./pages/Committees/CommitteeListPage'));
const IntakeTriagePage = lazyWithRetry(() => import('./pages/Committees/IntakeTriagePage'));
const Bridge1Page = lazyWithRetry(() => import('./pages/Bridges/Bridge1Page'));
const BridgeTwoPage = lazyWithRetry(() => import('./pages/Bridges/BridgeTwoPage'));
const BridgeThreePage = lazyWithRetry(() => import('./pages/Bridges/BridgeThreePage'));
const BridgeFourWrapper = lazyWithRetry(() => import('./pages/Bridges/BridgeFourWrapper'));
const BridgeOverviewPage = lazyWithRetry(() => import('./pages/Bridges/BridgeOverviewPage'));
const PMOPipelinePage = lazyWithRetry(() => import('./pages/PMO/PMOPipelinePage'));
const AuditPage = lazyWithRetry(() => import('./src/pages/Admin/AuditPage'));
const TenantManagementPage = lazyWithRetry(() => import('./pages/SuperAdmin/TenantManagementPage'));
const PlansPage = lazyWithRetry(() => import('./pages/Admin/PlansPage'));
const HomePublicPage = lazyWithRetry(() => import('./pages/HomePublic/HomePublic'));
const MagicApprovalPage = lazyWithRetry(() => import('./pages/MagicApproval/MagicApprovalPage'));

// Feature 013: Kanban & Gantt Views
const KanbanPage = lazyWithRetry(() => import('./pages/Project/KanbanPage'));
const GanttPage = lazyWithRetry(() => import('./pages/Project/GanttPage'));
const ProjectViewLayout = lazyWithRetry(() => import('./pages/Project/ProjectViewLayout'));

// Feature 001: OKR & Programs Management
const ProgramsPage = lazyWithRetry(() => import('./src/pages/Programs'));
const OkrsPage = lazyWithRetry(() => import('./src/pages/Okrs'));
const OkrDashboardPage = lazyWithRetry(() => import('./src/pages/Okrs/Dashboard'));

function RootLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenantId } = useTenantContext();

  const isIndexRoute = location.pathname === '/';
  const isPublicHomepage = location.pathname === '/home-public';
  const publicPaths = new Set<string>(['/','/home-public','/faq','/privacidade','/termos','/cookies']);

  // Routes that should not use the internal app Layout
  if (isIndexRoute || isPublicHomepage || publicPaths.has(location.pathname)) {
    return (
      <Suspense fallback={<div>Carregando...</div>}>
        <Outlet />
      </Suspense>
    );
  }

  // Superadmins need a specific tenant selected; regular users always have tenant_id in JWT
  const showChat = user?.is_approved && (!user?.is_superadmin || selectedTenantId !== null);

  return (
    <Layout>
      <TourController />
      <Suspense fallback={<div>Carregando...</div>}>
        <Outlet />
      </Suspense>
      {showChat && (
        <>
          <FloatingChatButton />
          <ChatDrawer />
        </>
      )}
    </Layout>
  );
}

export function Router() {
  const futureConfig: any = {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  };
  const router = createBrowserRouter([
    {
      path: '/',
      element: <RootLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        { index: true, element: <HomePublicPage /> }, // PUBLIC: Root redirects to homepage comercial
        { path: 'home-public', element: <HomePublicPage /> }, // PUBLIC: Homepage comercial (sem autenticação)
        { path: 'home', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/projetos"><HomePage /></ProtectedRoute> }, // PROTECTED: Authenticated home (edit_read+)
        // Auth
        { path: 'login', element: <LoginPage /> },
        { path: 'registrar', element: <RegisterPage /> },
        { path: 'signup', element: <RegisterPage /> }, // Alias for registrar
        { path: 'approve/:token', element: <Suspense fallback={null}><MagicApprovalPage /></Suspense> },
        { path: 'esqueci-minha-senha', element: <ForgotPasswordPage /> },
        { path: 'reset-password', element: <ResetPasswordPage /> },
        // Admin
        { path: 'admin/aprovacoes', element: <ProtectedRoute permission="admin"><UserApprovalsPage /></ProtectedRoute> },
        { path: 'admin/auditoria', element: <ProtectedRoute permission="admin"><FeatureGuard feature="audit"><AuditPage /></FeatureGuard></ProtectedRoute> },
        { path: 'admin/alerts', element: <ProtectedRoute permission="admin"><AlertsConfigPage /></ProtectedRoute> },
        { path: 'admin/settings/general', element: <ProtectedRoute permission="admin"><SettingsPage /></ProtectedRoute> },
        { path: 'admin/planos', element: <ProtectedRoute permission="admin"><PlansPage /></ProtectedRoute> },
        // SuperAdmin
        { path: 'superadmin/tenants', element: <ProtectedRoute permission="admin"><TenantManagementPage /></ProtectedRoute> },
        // App
  { path: 'projetos', element: <ProtectedRoute><ProjectsPage /></ProtectedRoute> },
  { path: 'projetos/roadmap', element: <ProtectedRoute permission="admin"><ProjectsRoadmapPage /></ProtectedRoute> },
  { path: 'projects', element: <ProtectedRoute><ProjectsPage /></ProtectedRoute> }, // English alias for deeplinks
  { path: 'projects/roadmap', element: <ProtectedRoute permission="admin"><ProjectsRoadmapPage /></ProtectedRoute> },
  { path: 'projeto', element: <ProtectedRoute><ProjectViewLayout><ProjectPage /></ProjectViewLayout></ProtectedRoute> },
  { path: 'projects/:projectId', element: <ProtectedRoute><ProjectViewLayout><ProjectPage /></ProjectViewLayout></ProtectedRoute> }, // Deeplink project detail
  { path: 'projects/:projectId/kanban', element: <ProtectedRoute><FeatureGuard feature="kanban"><ProjectViewLayout><KanbanPage /></ProjectViewLayout></FeatureGuard></ProtectedRoute> }, // Feature 013: Kanban view
  { path: 'projects/:projectId/gantt', element: <ProtectedRoute><FeatureGuard feature="gantt"><ProjectViewLayout><GanttPage /></ProjectViewLayout></FeatureGuard></ProtectedRoute> }, // Feature 013: Gantt view
  { path: 'projects/:projectId/tasks/:taskId', element: <ProtectedRoute><ProjectPage /></ProtectedRoute> }, // Deeplink task detail
  { path: 'projects/:projectId/files/:fileId', element: <ProtectedRoute><ProjectPage /></ProtectedRoute> }, // Deeplink document detail
  { path: 'relatorios', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><ReportsPage /></ProtectedRoute> },
  { path: 'relatorios/portfolio-ia', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><FeatureGuard feature="dashboard_executive"><PortfolioAIReportPage /></FeatureGuard></ProtectedRoute> },
  { path: 'perfil', element: <ProtectedRoute><UserProfilePage /></ProtectedRoute> },
  { path: 'cadastros/projetos', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><ProjectCreatePage /></ProtectedRoute> },
  { path: 'cadastros/destaques', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><div>Pagina de Cadastros - Destaques (em desenvolvimento)</div></ProtectedRoute> },
  { path: 'cadastros/responsaveis', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><ResponsibleRegistryPage /></ProtectedRoute> },
  { path: 'cadastros/grupos-custo', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><FeatureGuard feature="cost_management"><CostGroupRegistryPage /></FeatureGuard></ProtectedRoute> },
  { path: 'cadastros/faq', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><div>Pagina de Cadastros - FAQ (em desenvolvimento)</div></ProtectedRoute> },
  { path: 'cadastros/programas', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><ProgramsPage /></ProtectedRoute> },
  { path: 'cadastros/okrs', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><OkrsPage /></ProtectedRoute> },
  { path: 'okrs/dashboard', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><OkrDashboardPage /></ProtectedRoute> },
  { path: 'faq', element: <PublicFAQPage /> },
  { path: 'privacidade', element: <PublicPrivacyPage /> },
  { path: 'termos', element: <PublicTermsPage /> },
  { path: 'cookies', element: <PublicCookiesPage /> },
    { path: 'app/intake', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><IntakeListPage /></ProtectedRoute> },
    { path: 'app/committees', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><CommitteeListPage /></ProtectedRoute> },
    { path: 'app/committee/approvals', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><IntakeTriagePage /></ProtectedRoute> },
      { path: 'projects/:projectId/bridges', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><BridgeOverviewPage /></ProtectedRoute> },
      { path: 'projects/:projectId/bridges/1', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><Bridge1Page /></ProtectedRoute> },
      { path: 'projects/:projectId/bridges/2', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><BridgeTwoPage /></ProtectedRoute> },
      { path: 'projects/:projectId/bridges/3', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><BridgeThreePage /></ProtectedRoute> },
      { path: 'projects/:projectId/bridges/4', element: <ProtectedRoute permission="edit_read_delete" redirectTo="/forbidden"><BridgeFourWrapper /></ProtectedRoute> },
      // Legacy routes — redirect to new structure
      { path: 'bridges/1/:projectId', element: <Navigate to="/projetos" replace /> },
      { path: 'bridges/2', element: <Navigate to="/projetos" replace /> },
      { path: 'bridges/3', element: <Navigate to="/projetos" replace /> },
      { path: 'bridges/4', element: <Navigate to="/projetos" replace /> },
      { path: 'pmo/pipeline', element: <ProtectedRoute permission="admin" redirectTo="/forbidden"><PMOPipelinePage /></ProtectedRoute> },
      { path: 'forbidden', element: <ForbiddenPage /> },
        { path: '*', element: <Navigate to="/projetos" replace /> },
      ],
    },
  ], { future: futureConfig });

  return (
    <RouterProvider
      router={router}
      future={futureConfig}
    />
  );
}
